/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
  LitElement,
  html,
  property,
  CSSResultArray,
  TemplateResult,
} from 'lit-element';
import { customElement } from 'lit/decorators.js';
import { styles } from './nile-checkbox.css';

import { classMap } from 'lit/directives/class-map.js';
import { query, state } from 'lit/decorators.js';
import { defaultValue } from '../internal/default-value';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { watch } from '../internal/watch';
import NileElement from '../internal/nile-element';
import type { CSSResultGroup } from 'lit';

/**
 * @summary Checkboxes allow the user to toggle an option on or off.
 *
 * @dependency nile-icon
 *
 * @slot - The checkbox's label.
 *
 * @event nile-blur - Emitted when the checkbox loses focus.
 * @event nile-change - Emitted when the checked state changes.
 * @event nile-focus - Emitted when the checkbox gains focus.
 * @event nile-input - Emitted when the checkbox receives input.
 * @event nile-invalid - Emitted when the form control has been checked for validity and its constraints aren't satisfied.
 *
 * @csspart base - The component's base wrapper.
 * @csspart control - The square container that wraps the checkbox's checked state.
 * @csspart control--checked - Matches the control part when the checkbox is checked.
 * @csspart control--indeterminate - Matches the control part when the checkbox is indeterminate.
 * @csspart checked-icon - The checked icon, an `<nile-icon>` element.
 * @csspart indeterminate-icon - The indeterminate icon, an `<nile-icon>` element.
 * @csspart label - The container that wraps the checkbox's label.
 */

/**
 * Nile icon component.
 *
 * @tag nile-checkbox
 *
 */
@customElement('nile-checkbox')
export class NileCheckbox extends NileElement {
  constructor() {
    super();
  }

  static styles: CSSResultGroup = styles;

  @query('input[type="checkbox"]') input: HTMLInputElement;

  @state() private hasFocus = false;

  @property() title = ''; // make reactive to pass through

  /** The name of the checkbox, submitted as a name/value pair with form data. */
  @property() name = '';

  /** The current value of the checkbox, submitted as a name/value pair with form data. */
  @property() value: boolean;

  /** The checkbox's size. */
  @property({ reflect: true }) size: 'small' | 'medium' | 'large' = 'medium';

  /** Disables the checkbox. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Draws the checkbox in a checked state. */
  @property({ type: Boolean, reflect: true }) checked = false;

  /** Label, declared this property for backward compatibility of old component  */
  @property({ type: Boolean, reflect: true }) label = '';

  /**
   * Draws the checkbox in an indeterminate state. This is usually applied to checkboxes that represents a "select
   * all/none" behavior when associated checkboxes have a mix of checked and unchecked states.
   */
  @property({ type: Boolean, reflect: true }) indeterminate = false;

  /** The default value of the form control. Primarily used for resetting the form control. */
  @defaultValue('checked') defaultChecked = false;

  /**
   * By default, form controls are associated with the nearest containing `<form>` element. This attribute allows you
   * to place the form control outside of a form and associate it with the form that has this `id`. The form must be in
   * the same document or shadow root for this to work.
   */
  @property({ reflect: true }) form = '';

  /** Makes the checkbox a required field. */
  @property({ type: Boolean, reflect: true }) required = false;

  private handleClick() {
    this.checked = !this.checked;
    this.indeterminate = false;
    this.dispatchEvent(
      new CustomEvent('valueChange', {
        composed: true,
        bubbles: true,
        detail: {
          checked: this.checked,
        },
      })
    );
  }

  private handleBlur() {
    this.hasFocus = false;
    this.emit('blur');
  }

  private handleInput() {
    this.emit('input');
  }

  private handleFocus() {
    this.hasFocus = true;
    this.emit('focus');
  }

  @watch(['checked', 'indeterminate'], { waitUntilFirstUpdate: true })
  handleStateChange() {
    this.input.checked = this.checked; // force a sync update
    this.input.indeterminate = this.indeterminate; // force a sync update
  }

  /** Simulates a click on the checkbox. */
  click() {
    this.input.click();
  }

  /** Sets focus on the checkbox. */
  focus(options?: FocusOptions) {
    this.input.focus(options);
  }

  /** Removes focus from the checkbox. */
  blur() {
    this.input.blur();
  }


  render() {
    return html`
      <label
        part="base"
        class=${classMap({
          checkbox: true,
          'checkbox--checked': this.checked,
          'checkbox--disabled': this.disabled,
          'checkbox--focused': this.hasFocus,
          'checkbox--indeterminate': this.indeterminate,
          'checkbox--medium': this.size === 'medium',
        })}
      >
        <input
          class="checkbox__input"
          type="checkbox"
          title=${
            this
              .title /* An empty title prevents browser validation tooltips from appearing on hover */
          }
          name=${this.name}
          value=${ifDefined(this.value)}
          .indeterminate=${live(this.indeterminate)}
          .checked=${live(this.checked)}
          .disabled=${this.disabled}
          .required=${this.required}
          aria-checked=${this.checked ? 'true' : 'false'}
          @click=${this.handleClick}
          @input=${this.handleInput}
          @blur=${this.handleBlur}
          @focus=${this.handleFocus}
        />

        <span
          part="control${this.checked ? ' control--checked' : ''}${this
            .indeterminate
            ? ' control--indeterminate'
            : ''}"
          class="checkbox__control"
        >
          ${this.checked
            ? html`
                <nile-icon
                  part="checked-icon"
                  class="checkbox__checked-icon"
                  color="white"
                  library="system"
                  name="tick"
                  size="12"
                ></nile-icon>
              `
            : ''}
          ${!this.checked && this.indeterminate
            ? html`
                <nile-icon
                  part="indeterminate-icon"
                  class="checkbox__indeterminate-icon"
                  library="system"
                  color="white"
                  name="minus"
                  size="12"
                ></nile-icon>
              `
            : ''}
        </span>

        <div part="label" class="checkbox__label">
          ${typeof this.label === 'boolean' ? '' : this.label}
          <slot></slot>
        </div>
      </label>
    `;
  }
}

export default NileCheckbox;

declare global {
  interface HTMLElementTagNameMap {
    'nile-checkbox': NileCheckbox;
  }
}
